import { useCallback, useEffect, useState } from "react";
import { ReactComponent as CarouselBase } from "../../assets/images/CarouselBase.svg";
import { pointsToPathString } from "../../modules/patients/components/painAssessment/pain-assesment.utils";
import "../carouselNavigation/CarouselNavigation.css";
import {
  DEFAULT_TOOL,
  getPathsToDelete,
  getToolConfig,
} from "../painAssessmentToolSelector/painAssesmentConfig";
import "./SvgImageCarousel.css";
import {
  getCoordinatesFromEvent,
  getSvgComponentFromItem,
} from "./svgImageCarouselHelpers";

const SvgImageCarousel = ({
  svgComponents,
  activeIndex,
  onSvgClick,
  isDisabled = false,
  allPaths = [],
  selectedPathIndex = null,
  onPathClick,
  onPathHover,
  onPathLeave,
  onPathAdded,
  onPathsDeleted,
  canDraw = true,
  selectedTool = DEFAULT_TOOL,
}) => {
  const hasSvgs = svgComponents && svgComponents.length > 0;

  // Initialize state with activeIndex
  const [currentIndex, setCurrentIndex] = useState(() => {
    if (hasSvgs && activeIndex !== null && activeIndex < svgComponents.length) {
      return activeIndex;
    }
    return 0;
  });

  const [isDrawing, setIsDrawing] = useState(false);
  const [currentPath, setCurrentPath] = useState([]);
  const [startPoint, setStartPoint] = useState(null);

  const resolveStrokeWidth = useCallback(pathObj => {
    const parsed = Number(pathObj?.strokeWidth);
    if (Number.isFinite(parsed) && parsed > 0) return parsed;

    const toolConfig = getToolConfig(pathObj?.toolType || DEFAULT_TOOL);
    return toolConfig.strokeWidth || 3;
  }, []);

  useEffect(() => {
    if (hasSvgs && activeIndex !== null && activeIndex < svgComponents.length) {
      setCurrentIndex(activeIndex);
    }
  }, [activeIndex, hasSvgs, svgComponents?.length]);

  const handlePrevClick = () => {
    if (!hasSvgs) return;
    const newIndex =
      currentIndex === svgComponents.length - 1 ? 0 : currentIndex + 1;
    setCurrentIndex(newIndex);
    if (onSvgClick) onSvgClick(newIndex);
  };

  const handleNextClick = () => {
    if (!hasSvgs) return;
    const newIndex =
      currentIndex === 0 ? svgComponents.length - 1 : currentIndex - 1;
    setCurrentIndex(newIndex);
    if (onSvgClick) onSvgClick(newIndex);
  };

  // Drawing event handlers - all here
  const handleMouseDown = useCallback(
    event => {
      if (!canDraw) return;

      // Check if clicking on existing path
      if (event.target.classList.contains("pain-recorded-path")) {
        return;
      }

      if (
        selectedTool !== "eraser" &&
        event.target.classList.contains("pain-current-path")
      )
        return;

      event.preventDefault();
      event.stopPropagation();
      setIsDrawing(true);
      const pos = getCoordinatesFromEvent(event);
      const toolConfig = getToolConfig(selectedTool);
      const newPath = toolConfig.onStart({ pos });
      setStartPoint(pos);
      setCurrentPath(newPath);
    },
    [canDraw, selectedTool]
  );

  const handleMouseMove = useCallback(
    event => {
      if (!isDrawing || !canDraw) return;
      event.preventDefault();
      event.stopPropagation();
      const pos = getCoordinatesFromEvent(event);
      const toolConfig = getToolConfig(selectedTool);
      const newPath = toolConfig.onMove({ pos, currentPath, startPoint });
      setCurrentPath(newPath);
    },
    [isDrawing, canDraw, selectedTool, currentPath, startPoint]
  );

  const handleMouseUp = useCallback(() => {
    if (!isDrawing) return;

    setIsDrawing(false);

    const toolConfig = getToolConfig(selectedTool);
    const result = toolConfig.onEnd({
      currentPath,
      startPoint,
      allPaths,
      currentIndex,
      getPathsToDelete,
    });

    // Handle eraser deletion
    if (result?.delete) {
      if (onPathsDeleted && result.delete.length > 0) {
        onPathsDeleted(result.delete);
      }
    }
    // Handle path creation
    else if (Array.isArray(result) && result.length > 1) {
      if (onPathAdded) {
        onPathAdded(result, currentIndex, selectedTool);
      }
    }

    setCurrentPath([]);
    setStartPoint(null);
  }, [
    isDrawing,
    currentPath,
    currentIndex,
    selectedTool,
    startPoint,
    onPathAdded,
    onPathsDeleted,
    allPaths,
  ]);

  // Get the current SVG component with drawing capabilities
  const getCurrentSvgComponent = () => {
    if (!hasSvgs || !svgComponents[currentIndex]) return null;

    const svgItem = svgComponents[currentIndex];
    const OriginalSvgComponent = getSvgComponentFromItem(svgItem);

    if (!OriginalSvgComponent) return null;

    // Get paths for current orientation
    const currentOrientationPaths = allPaths.filter(
      pathObj => pathObj.orientation === currentIndex
    );

    return (
      <div className="carousel-svg-image" key={currentIndex}>
        <svg
          className="anatomy-svg-with-drawing"
          style={{
            cursor: canDraw ? getToolConfig(selectedTool).cursor : "default",
          }}
          viewBox="0 0 215 584"
          preserveAspectRatio="xMidYMid meet"
          onMouseDown={handleMouseDown}
          onMouseMove={handleMouseMove}
          onMouseUp={handleMouseUp}
          onMouseLeave={handleMouseUp}
          onTouchStart={handleMouseDown}
          onTouchMove={handleMouseMove}
          onTouchEnd={handleMouseUp}
        >
          {/* Original anatomy SVG content */}
          <OriginalSvgComponent />

          {/* Drawing paths for current orientation */}
          {currentOrientationPaths.map((pathObj, index) => {
            const globalIndex = allPaths.indexOf(pathObj);
            const strokeWidth = resolveStrokeWidth(pathObj);
            return (
              <path
                key={`recorded-${globalIndex}`}
                d={pointsToPathString(pathObj.path)}
                stroke={pathObj.color || "#ffffff"}
                fill="none"
                strokeWidth={strokeWidth}
                strokeLinecap="round"
                strokeLinejoin="round"
                vectorEffect="non-scaling-stroke"
                className={`pain-recorded-path ${
                  selectedPathIndex === globalIndex ? "selected" : ""
                }`}
                onClick={e => {
                  e.preventDefault();
                  e.stopPropagation();
                  if (onPathClick) onPathClick(globalIndex, e);
                }}
                onMouseEnter={e => {
                  if (onPathHover) onPathHover(pathObj, e);
                }}
                onMouseMove={e => {
                  if (onPathHover) onPathHover(pathObj, e);
                }}
                onMouseLeave={() => {
                  if (onPathLeave) onPathLeave();
                }}
                style={{
                  cursor: "pointer",
                  pointerEvents: "stroke",
                  strokeWidth,
                }}
              />
            );
          })}

          {/* Current drawing path */}
          {(() => {
            // Only show preview if we have a startPoint and currentPath
            if (!startPoint || !currentPath || currentPath.length === 0)
              return null;

            const toolConfig = getToolConfig(selectedTool);
            const previewPath = toolConfig.preview({ startPoint, currentPath });

            if (!previewPath || previewPath.length < 2) return null;

            const previewStrokeWidth = toolConfig.strokeWidth || 2;

            return (
              <path
                d={pointsToPathString(previewPath)}
                stroke="#ffffff"
                fill="none"
                strokeWidth={previewStrokeWidth}
                strokeLinecap="round"
                strokeLinejoin="round"
                vectorEffect="non-scaling-stroke"
                className="pain-current-path"
              />
            );
          })()}
        </svg>
      </div>
    );
  };

  return (
    <div className="svg-image-carousel">
      <div className="carousel-svg-image-container">
        {getCurrentSvgComponent()}
      </div>

      <div className="carousel-navigation">
        <CarouselBase className="carousel-base" />
        {!isDisabled && (
          <button
            className="carousel-arrow-button carousel-prev-button"
            onClick={handlePrevClick}
            aria-label="Previous SVG"
          />
        )}
        {!isDisabled && (
          <button
            className="carousel-arrow-button carousel-next-button"
            onClick={handleNextClick}
            aria-label="Next SVG"
          />
        )}
      </div>
    </div>
  );
};

export default SvgImageCarousel;
